<template>
  <div
    v-on-click-outside="() => (showMenu = false)"
    class="flex items-center justify-start space-x-2"
  >
    <div class="text-xs">{{ $t("DEALS.DEAL_NOTES.TYPE_OF_NOTE") }}:</div>
    <lf-pill
      data-cy="note-type-toggle"
      size="sm"
      class="relative h-[22px] shadow-fab"
      :class="disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'"
      color-index="23"
      @click="toggleMenu"
    >
      <div class="flex items-center space-x-2">
        <div class="w-22 truncate text-left">
          <span class="text-xs text-headline">
            {{ noteTypes[noteType].title }}
          </span>
        </div>
        <icon-base
          :icon="IconArrowUp"
          icon-color="#FF6D04"
          height="10"
          width="10"
          :class="{ '-translate-y-1 rotate-180': showMenu }"
        />
      </div>
    </lf-pill>
    <div
      v-if="showMenu"
      data-cy="note-type-modal"
      class="note-type-menu absolute top-[34px] z-2 w-[280px] space-y-2 rounded bg-white p-2 shadow-popover"
    >
      <div
        v-for="(type, key) in availableNoteTypes"
        :key="key"
        data-cy="note-type-option"
        class="flex cursor-pointer items-center space-x-3 self-stretch rounded p-2 hover:bg-primary-background-active"
        :class="{ 'bg-primary-background-active': noteType === type.type }"
        @click="selectType(type.type)"
      >
        <div
          class="relative flex h-10 w-10 flex-none items-center justify-center gap-2.5 rounded-full bg-flush-orange"
        >
          <icon-base :icon="IconNotes" class="text-white" />
          <div
            v-if="type.icon"
            class="absolute -bottom-[9px] -right-[9px] flex h-6 w-6 rounded-full border-2 border-divider p-1"
            :class="type.icon?.class"
          >
            <icon-base :icon="type.icon.component" width="12" height="12" />
          </div>
        </div>
        <div class="flex flex-grow flex-col items-start">
          <span class="font-semibold text-headline">{{ type.title }}</span>
          <span>{{ type.subtitle }}</span>
        </div>
        <icon-base
          v-if="noteType === type.type"
          :icon="IconCheck"
          class="flex-none text-primary"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from "vue";
import { useI18n } from "vue-i18n";
import type { Note } from "@/models/notes";
import LfPill from "@/components/ui/text/LfPill.vue";
import IconArrowUp from "@/components/icons/IconArrowUp.vue";
import IconNotes from "@/components/icons/IconNotes.vue";
import IconCheck from "@/components/icons/IconCheck.vue";
import IconBusiness from "@/components/icons/IconBusiness.vue";
import IconTelephone from "@/components/icons/IconTelephone.vue";
import { NoteType } from "@/enums/notes";

const emit = defineEmits<{
  "type:update": [NoteType];
}>();

const props = defineProps<{
  noteToEdit?: Note | null;
  noteType: NoteType;
  disabled?: boolean;
}>();

const { t } = useI18n();

const noteTypes = {
  application: {
    title: t("DEALS.NOTES.DEAL_NOTE"),
    icon: undefined,
    subtitle: t("DEALS.NOTES.STRATEGIC_INSIGHTS"),
    type: NoteType.Application
  },
  business: {
    title: t("DEALS.NOTES.BUSINESS_NOTE"),
    icon: {
      component: IconBusiness,
      class: "bg-secondary-purple text-white"
    },
    subtitle: t("DEALS.NOTES.BUSINESS_UPDATES"),
    type: NoteType.Business
  },
  call: {
    title: t("DEALS.NOTES.CALL_NOTE"),
    icon: {
      component: IconTelephone,
      class: "bg-secondary-green text-white"
    },
    subtitle: t("DEALS.NOTES.CALL_SUMMARY"),
    type: NoteType.Call
  }
};

const showMenu = ref(false);

const availableNoteTypes = computed(() => {
  if (props.noteToEdit?.id) {
    return noteTypes;
  }
  // @TODO: Restore call note type for new notes when we have call sessions from BE
  return Object.fromEntries(
    Object.entries(noteTypes).filter(([key]) => key !== "call")
  );
});

const toggleMenu = () => {
  if (props.disabled) {
    return;
  }
  showMenu.value = !showMenu.value;
};

const selectType = (type: NoteType) => {
  emit("type:update", type);
  showMenu.value = false;
};
</script>
